import React, { useRef, useCallback, useState, useEffect } from "react";
import {
  Bold,
  Italic,
  Underline,
  Link2,
  Type,
  Minus,
} from "lucide-react";

interface SignatureEditorProps {
  value: string;
  onChange: (html: string) => void;
  compact?: boolean; // smaller toolbar for inline use
  placeholder?: string; // shown when content is empty
}

const FONT_SIZES = [
  { label: "Small", value: "1" },
  { label: "Normal", value: "3" },
  { label: "Large", value: "5" },
];

const COLORS = [
  { label: "Dark", value: "#1e293b" },
  { label: "Green", value: "#0F5132" },
  { label: "Gray", value: "#64748b" },
  { label: "Light", value: "#94a3b8" },
  { label: "Blue", value: "#1d4ed8" },
  { label: "Red", value: "#dc2626" },
];

const SignatureEditor: React.FC<SignatureEditorProps> = ({ value, onChange, compact = false, placeholder }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const internalHtmlRef = useRef<string>(value);
  const [showSizes, setShowSizes] = useState(false);
  const [showColors, setShowColors] = useState(false);

  // Only sync from props when the value changed externally (not from user typing)
  useEffect(() => {
    if (editorRef.current && value !== internalHtmlRef.current) {
      internalHtmlRef.current = value;
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const exec = useCallback((command: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    if (editorRef.current) {
      internalHtmlRef.current = editorRef.current.innerHTML;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      internalHtmlRef.current = editorRef.current.innerHTML;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleLink = useCallback(() => {
    const url = window.prompt("Enter URL:");
    if (url) {
      exec("createLink", url);
    }
  }, [exec]);

  const handleHr = useCallback(() => {
    exec("insertHTML", '<hr style="border: none; border-top: 1px solid #e2e8f0; margin: 8px 0;" />');
  }, [exec]);

  const btnClass = compact
    ? "p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
    : "p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors";

  const iconSize = compact ? "h-3 w-3" : "h-3.5 w-3.5";

  return (
    <div className="relative">
      {/* Toolbar */}
      <div className={`flex items-center gap-0.5 ${compact ? "px-2 py-1 border-b border-slate-100" : "px-3 py-1.5 border-b border-slate-200 bg-slate-50 rounded-t-xl"}`}>
        <button type="button" onClick={() => exec("bold")} className={btnClass} title="Bold">
          <Bold className={iconSize} />
        </button>
        <button type="button" onClick={() => exec("italic")} className={btnClass} title="Italic">
          <Italic className={iconSize} />
        </button>
        <button type="button" onClick={() => exec("underline")} className={btnClass} title="Underline">
          <Underline className={iconSize} />
        </button>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        {/* Font size */}
        <div className="relative">
          <button type="button" onClick={() => { setShowSizes(!showSizes); setShowColors(false); }} className={btnClass} title="Font size">
            <Type className={iconSize} />
          </button>
          {showSizes && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50 min-w-[90px]">
              {FONT_SIZES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => { exec("fontSize", s.value); setShowSizes(false); }}
                  className="block w-full text-left px-3 py-1 text-xs text-slate-600 hover:bg-slate-50"
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Text color */}
        <div className="relative">
          <button type="button" onClick={() => { setShowColors(!showColors); setShowSizes(false); }} className={btnClass} title="Text color">
            <div className={`${compact ? "h-3 w-3" : "h-3.5 w-3.5"} rounded-sm border border-slate-300 bg-gradient-to-br from-emerald-700 to-slate-600`} />
          </button>
          {showColors && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 p-2 z-50">
              <div className="grid grid-cols-3 gap-1">
                {COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => { exec("foreColor", c.value); setShowColors(false); }}
                    className="group flex items-center gap-1.5 px-2 py-1 rounded hover:bg-slate-50"
                    title={c.label}
                  >
                    <div className="h-3 w-3 rounded-full border border-slate-200" style={{ backgroundColor: c.value }} />
                    <span className="text-[10px] text-slate-500">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <button type="button" onClick={handleLink} className={btnClass} title="Insert link">
          <Link2 className={iconSize} />
        </button>
        <button type="button" onClick={handleHr} className={btnClass} title="Divider line">
          <Minus className={iconSize} />
        </button>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        className={`outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none ${compact ? "px-3 py-2 text-[12px] min-h-[60px] max-h-[150px] overflow-y-auto" : "px-4 py-3 text-sm min-h-[120px] max-h-[300px] overflow-y-auto"}`}
        style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", lineHeight: 1.5 }}
      />
    </div>
  );
};

export default SignatureEditor;
